/**
 * ISG Intranet — Frontend Entry Point
 *
 * Uses @isg/ui-kit for shared CSS, components, and services.
 */

// --- Styles (from @isg/ui-kit) ---
import '@isg/ui-kit/css/tokens.css';
import '@isg/ui-kit/css/reset.css';
import '@isg/ui-kit/css/components.css';
import '@isg/ui-kit/css/layout.css';
import './styles/tiptap.css';
import './styles/hover.css';
import './styles/global-search.css';
import './styles/media.css';
import './styles/org-chart.css';
import './styles/kiosk.css';

// --- Services & Utils (from @isg/ui-kit) ---
import { router, store, auth, api , checkEnvBanner } from '@isg/ui-kit';
import { maybeForcePasswordChange } from '@isg/ui-kit';
import { syncPushSubscription } from '@isg/ui-kit';
import { renderNavigation, renderMobileHeader, createSidebarOverlay } from '@isg/ui-kit';
import { applyBranding, loadBranding } from '@isg/ui-kit';
import { initTheme, applyTheme } from '@isg/ui-kit';
import { initAppUpdate } from '@isg/ui-kit';

/* SSO: capture handoff from intranet broker */
auth.captureSSOHandoff();

// --- i18n ---
import { t, initI18n } from './i18n/index.js';

// --- Components ---
import { createNotificationBell, createNotificationSidebar } from './components/notification-sidebar.js';
import { createGlobalSearch } from './components/global-search.js';
import { createProfileDropdown } from './components/profile-dropdown.js';
import { checkCelebrations } from './components/celebration-overlay.js';
import { createKioskBar } from './components/kiosk-bar.js';
import { isKioskSession } from './kiosk.js';

// --- Lazy-loaded pages ---
const pages = {
  dashboard:     () => import('./pages/dashboard.js'),
  launcher:      () => import('./pages/launcher.js'),
  directory:     () => import('./pages/directory.js'),
  announcements: () => import('./pages/announcements.js'),
  groups:        () => import('./pages/groups.js'),
  groupDetail:   () => import('./pages/group-detail.js'),
  documents:     () => import('./pages/documents.js'),
  events:        () => import('./pages/events.js'),
  polls:         () => import('./pages/polls.js'),
  media:         () => import('./pages/media.js'),
  huddle:        () => import('./pages/huddle.js'),
  profile:       () => import('./pages/profile.js'),
  helpdesk:      () => import('./pages/helpdesk.js'),
  betaFeedback:  () => import('./pages/beta-feedback.js'),
  notifications: () => import('./pages/notifications.js'),
  calendar:      () => import('./pages/calendar.js'),
  settings:      () => import('./pages/settings.js'),
  login:         () => import('./pages/login.js'),
  betaSignup:    () => import('./public/beta-signup.js'),
  eventRsvp:     () => import('./public/event-rsvp.js'),
  eventAttend:   () => import('./public/event-attend.js'),
  formFill:      () => import('./public/form-fill.js'),
  raiseTicket:   () => import('./public/raise-ticket.js'),
  engagement:    () => import('./pages/engagement.js'),
  markets:       () => import('./pages/markets.js'),
  digest:        () => import('./pages/digest.js'),
};

// Public routes — reachable without a session (e.g. the QR-code beta sign-up).
const PUBLIC_PATHS = ['/login', '/beta-signup', '/event-rsvp', '/form', '/attend', '/raise'];

/** Public routes are matched by PREFIX, not equality: the QR RSVP path
 *  carries its event token in the path (/event-rsvp/<token>), so an exact
 *  `includes` would send every scanned poster to the login screen — which is
 *  precisely the wall this feature exists to get around. */
const isPublicPath = (p) =>
  PUBLIC_PATHS.some((base) => p === base || p.startsWith(base + '/'));

// ── Navigation config ────────────────────────────────────────────────
// Built as a FUNCTION, not a module-scope const.
//
// ⚠️ These labels are t() calls. A module-scope const evaluates them during
// the import graph — before initI18n() has resolved the user's language — so
// the sidebar would freeze into whatever was registered at import time and
// stay English while the entire rest of the page rendered in German. It
// builds and throws nothing; the only way to see it is to open the page.
// Call this from renderAppShell(), which runs after the locale is settled.
/** Mirrors the server's `can_manage_social`: platform admin, BU admin, or the
 *  social_manager role on app_access.intranet. The SERVER is the control —
 *  this only decides whether to draw the link. */
function canManageSocial() {
  const u = store.get('user') || {};
  if (u.role === 'admin') return true;
  const access = (u.app_access || {}).intranet || {};
  const roles = access.roles || [];
  return Array.isArray(roles) && roles.includes('social_manager');
}

function buildNavConfig() {
  return [
    {
      section: null,
      items: [
        { path: '/', label: t('nav.dashboard'), icon: 'layout-dashboard' },
        { path: '/apps', label: t('nav.app_launcher'), icon: 'grid' },
      ],
    },
    {
      section: t('nav.content'),
      items: [
        { path: '/announcements', label: t('nav.announcements'), icon: 'megaphone' },
        { path: '/documents', label: t('nav.documents'), icon: 'folder' },
        { path: '/events', label: t('nav.events'), icon: 'calendar' },
        { path: '/calendar', label: t('nav.calendar'), icon: 'calendar-days' },
        { path: '/polls', label: t('nav.polls'), icon: 'bar-chart-2' },
        { path: '/media', label: t('nav.media'), icon: 'image' },
        // ⚠️ Sits next to Media because that is literally what it is —
        // a media album with a daily shape. Interim, until the huddle
        // runs from isg-bi; removing it is removing this line.
        { path: '/huddle', label: t('nav.huddle'), icon: 'users' },
        { path: '/markets', label: t('nav.markets'), icon: 'trending-up' },
        { path: '/digest', label: t('nav.digest'), icon: 'file-text' },
        { path: '/groups', label: t('nav.groups'), icon: 'message-circle' },
      ],
    },
    // Engagement workspace — social managers and platform admins only.
    // Built conditionally rather than route-guarded alone: a nav entry that
    // bounces you is worse than no nav entry.
    ...(canManageSocial() ? [{
      section: t('nav.engagement'),
      items: [
        { path: '/engagement', label: t('nav.engagement'), icon: 'trending-up' },
      ],
    }] : []),
    {
      section: t('nav.people'),
      items: [
        { path: '/directory', label: t('nav.staff_directory'), icon: 'users' },
      ],
    },
    {
      section: t('nav.support'),
      items: [
        { path: '/helpdesk', label: t('nav.raise_ticket'), icon: 'life-buoy' },
        { path: '/beta-feedback', label: t('nav.beta_feedback'), icon: 'message-square' },
      ],
    },
  ];
}

// ── Page transition helpers ───────────────────────────────────────────
const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function fadeOut(el) {
  if (prefersReducedMotion()) return Promise.resolve();
  el.style.transition = 'opacity 0.15s ease, transform 0.15s ease';
  el.style.opacity = '0';
  el.style.transform = 'translateY(6px)';
  return new Promise(r => setTimeout(r, 150));
}

function fadeIn(el) {
  if (prefersReducedMotion()) return;
  el.style.opacity = '0';
  el.style.transform = 'translateY(6px)';
  requestAnimationFrame(() => {
    el.style.transition = 'opacity 0.25s ease-out, transform 0.25s ease-out';
    el.style.opacity = '1';
    el.style.transform = 'translateY(0)';
  });
}

// ── Render a page module into #main-content ──────────────────────────
async function renderPage(loader, params = {}) {
  const main = document.getElementById('main-content');
  if (!main) return;

  // Fade out current content
  if (main.children.length > 0) {
    await fadeOut(main);
  }

  main.innerHTML = `
    <div class="main-content-inner" style="max-width:1280px;margin:0 auto;">
      <div class="skeleton-page">
        <div class="skeleton skeleton-heading" style="width:40%;margin-bottom:var(--space-5);"></div>
        <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:var(--space-4);margin-bottom:var(--space-6);">
          <div class="skeleton skeleton-stat"></div>
          <div class="skeleton skeleton-stat"></div>
          <div class="skeleton skeleton-stat"></div>
          <div class="skeleton skeleton-stat"></div>
        </div>
        <div class="skeleton skeleton-card" style="margin-bottom:var(--space-4);"></div>
        <div class="skeleton skeleton-card" style="margin-bottom:var(--space-4);"></div>
        <div class="skeleton skeleton-card"></div>
      </div>
    </div>
  `;
  fadeIn(main);

  try {
    const module = await loader();
    await fadeOut(main);
    main.innerHTML = '';
    main.style.opacity = '';
    main.style.transform = '';
    main.style.transition = '';
    await module.render(main, params);
    fadeIn(main);
  } catch (err) {
    console.error('Page render error:', err);
    main.innerHTML = `
      <div class="main-content-inner">
        <div class="empty-state">
          <p style="color: var(--red-600); font-weight: 500;">${t('common.something_went_wrong')}</p>
          <p>${err.message || t('common.failed_to_load_page')}</p>
          <button class="btn btn-primary mt-4" id="page-error-reload-btn">${t('common.reload')}</button>
        </div>
      </div>
    `;
    main.querySelector('#page-error-reload-btn')?.addEventListener('click', () => location.reload());
    main.style.opacity = '1';
    main.style.transform = '';
  }
}

// ── App shell ────────────────────────────────────────────────────────
function renderAppShell() {
  const app = document.getElementById('app');
  app.innerHTML = '';

  // Accent stripe at top
  const stripe = document.createElement('div');
  stripe.className = 'accent-stripe';
  app.appendChild(stripe);

  const layout = document.createElement('div');
  layout.className = 'app-layout';

  const mobileHeader = renderMobileHeader({ appTitle: 'ISG Intranet' });
  const overlay = createSidebarOverlay();

  const sidebar = renderNavigation({
    navConfig: buildNavConfig(),
    appTitle: 'ISG Intranet',
    fallbackInitial: 'I',
  });

  // Main content area
  const main = document.createElement('main');
  main.className = 'main-content';
  main.id = 'main-content';

  // ── Global Toolbar ──
  const toolbar = document.createElement('div');
  toolbar.className = 'global-toolbar';

  const toolbarLeft = document.createElement('div');
  toolbarLeft.className = 'global-toolbar-left';
  toolbarLeft.appendChild(createGlobalSearch());

  const toolbarRight = document.createElement('div');
  toolbarRight.className = 'global-toolbar-right';
  toolbarRight.appendChild(createNotificationBell());
  toolbarRight.appendChild(createProfileDropdown());

  toolbar.appendChild(toolbarLeft);
  toolbar.appendChild(toolbarRight);

  // ── Notification Sidebar (right panel) ──
  const notifSidebar = createNotificationSidebar();

  // ── Assemble layout ──
  layout.appendChild(mobileHeader);
  layout.appendChild(overlay);
  layout.appendChild(sidebar);

  const mainWrapper = document.createElement('div');
  mainWrapper.style.cssText = 'display:flex;flex-direction:column;flex:1;min-width:0;';
  mainWrapper.appendChild(toolbar);
  mainWrapper.appendChild(main);
  layout.appendChild(mainWrapper);

  // 🔴 E2: the kiosk bar is a SIBLING ABOVE `.app-layout`, never a child of
  // it. `.app-layout` is `display:flex` (a row), so appending the bar to it
  // made it a flex ITEM — a blue column down the left-hand side. It looked
  // wrong instantly on screen and not at all wrong to a test that only asked
  // whether the element existed.
  if (isKioskSession()) {
    app.classList.add('kiosk-mode');
    app.appendChild(createKioskBar());
  }
  app.appendChild(layout);
  app.appendChild(notifSidebar);

}

// ⚠️ WHOSE saved theme this browser has adopted — not "is a theme stored".
// ui-kit's initTheme() persists its 'light' fallback on every boot, the
// signed-out one before SSO included, so 'isg-theme' can never say "this
// browser has not chosen".
const THEME_SEEDED_FOR = 'intranet.themeSeededFor';

/* The first time a browser sees this person signed in, it takes the theme
   saved to their settings — so a kiosk (a wiped profile at every sign-in) or
   a new phone looks like their laptop. After that, this browser's own choice
   wins, as with the market prefs. ⚠️ Only an explicit light/dark is applied:
   'system' is also the column DEFAULT (160 of 164 rows, 2026-09-11), so
   honouring it would restyle a new browser for everyone who never chose. */
async function seedThemeFromSettings(user) {
  try {
    if (!user || localStorage.getItem(THEME_SEEDED_FOR) === String(user.id)) return;
  } catch { return; }                                   // private mode
  try {
    const settings = await api.get('/settings');
    if (settings && (settings.theme === 'light' || settings.theme === 'dark')) {
      applyTheme(settings.theme);
    }
    localStorage.setItem(THEME_SEEDED_FOR, String(user.id));
  } catch { /* not marked, so the next boot tries again; the default is fine meanwhile */ }
}

// ── Init ─────────────────────────────────────────────────────────────
// Paths only the SERVER can answer (Flask pages linked from emails). The
// service worker is told to pass them through, but a browser still running an
// older worker serves the app shell for them instead — so if we are ever
// booted on one, drop the stale worker and reload ONCE so the server answers.
const SERVER_ONLY = /^\/(rsvp|attend-reply)\//;

async function escapeStaleWorker() {
  if (!SERVER_ONLY.test(location.pathname) || !('serviceWorker' in navigator)) return false;
  const key = 'intranet.swEscape:' + location.pathname;
  try { if (sessionStorage.getItem(key)) return false; sessionStorage.setItem(key, '1'); } catch { /* reload anyway */ }
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.map((r) => r.unregister()));
  } catch { /* nothing to unregister */ }
  location.reload();
  return true;
}

async function init() {
  if (await escapeStaleWorker()) return;

  // Apply saved theme immediately to prevent flash
  initTheme();

  // ⚠️ Keep a long-lived tab on the current release. `registerSW.js` asks the
  // browser for an update exactly once, at load, so a tab left open all week
  // serves the bundle it started with and a fix shipped on Tuesday never
  // reaches it. This re-checks; a HIDDEN tab reloads itself, a visible one is
  // offered a refresh rather than having one taken under its fingers.
  initAppUpdate();

  // Pre-load branding so sidebar logo is ready
  await loadBranding().catch(() => {});

  // If returning from Entra SSO with a code, clear any existing session
  // so the login page renders and processes the callback
  const initParams = new URLSearchParams(window.location.search);
  if (initParams.get('code')) {
    api.token = null;
    store.set('user', null);
    store.set('isAuthenticated', false);
  }

  const hasSession = await auth.checkSession();

  // Runs alongside the language load and is awaited with it, so a seeded
  // theme lands before first paint rather than flipping the page after it.
  const themeSeed = hasSession ? seedThemeFromSettings(store.get('user')) : null;

  // Resolve the language BEFORE anything renders. The catalogue for a
  // non-English locale is a lazy chunk, so awaiting here is what prevents the
  // shell painting in English and visibly re-rendering a moment later.
  // Signed out we pass null and the engine falls back to localStorage, then to
  // the browser — so the login page is already in the right language.
  await initI18n(store.get('user')?.locale || null).catch(() => {});
  await themeSeed;

  // Re-register this browser's push subscription if it holds one. Push
  // services rotate endpoints, and the service worker's own
  // `pushsubscriptionchange` retry can't always send a CSRF token — so
  // this boot-time re-POST is the reliable repair. Deliberately not
  // awaited: it must never delay first paint, and it no-ops when the
  // user has never granted permission.
  // ⚠️ Never on a kiosk. The profile is wiped at sign-out, so a subscription
  // registered here belongs to a browser profile that will not exist in a few
  // minutes — it would leave an orphan on the server and could deliver one
  // person's notification to whoever is standing there next.
  if (hasSession && !isKioskSession()) syncPushSubscription('intranet');

  // Define routes
  router
    .on('/', (params) => renderPage(pages.dashboard, params))
    .on('/apps', (params) => renderPage(pages.launcher, params))
    .on('/directory', (params) => renderPage(pages.directory, params))
    .on('/announcements', (params) => renderPage(pages.announcements, params))
    .on('/groups', (params) => renderPage(pages.groups, params))
    .on('/groups/:slug', (params) => renderPage(pages.groupDetail, params))
    // A channel inside a group. ⚠️ `_match` compares SEGMENT COUNT first, so
    // this four-part pattern can never be shadowed by the two-part one above
    // however they are ordered — but keep them adjacent so nobody has to
    // rediscover that.
    .on('/groups/:slug/c/:channel', (params) => renderPage(pages.groupDetail, params))
    .on('/documents', (params) => renderPage(pages.documents, params))
    .on('/events', (params) => renderPage(pages.events, params))
    .on('/polls', (params) => renderPage(pages.polls, params))
    .on('/markets', (params) => renderPage(pages.markets, params))
    .on('/digest', (params) => renderPage(pages.digest, params))
    .on('/media', (params) => renderPage(pages.media, params))
    .on('/huddle', (params) => renderPage(pages.huddle, params))
    .on('/media/:id', (params) => renderPage(pages.media, params))
    .on('/profile', (params) => renderPage(pages.profile, params))
    .on('/profile/:id', (params) => renderPage(pages.profile, params))
    .on('/notifications', (params) => renderPage(pages.notifications, params))
    .on('/helpdesk', (params) => renderPage(pages.helpdesk, params))
    .on('/beta-feedback', (params) => renderPage(pages.betaFeedback, params))
    .on('/calendar', (params) => renderPage(pages.calendar, params))
    .on('/settings', (params) => renderPage(pages.settings, params))
    .on('/beta-signup', (params) => renderPage(pages.betaSignup, params))
    .on('/event-rsvp/:token', (params) => renderPage(pages.eventRsvp, params))
    .on('/raise', () => renderPage(pages.raiseTicket, {}))
    .on('/attend/:token', (params) => renderPage(pages.eventAttend, params))
    .on('/form/:token', (params) => renderPage(pages.formFill, params))
    .on('/engagement', (params) => renderPage(pages.engagement, params))
    .on('/login', (params) => renderPage(pages.login, params));

  // Auth guard
  router.before((path) => {
    // An AUTHENTICATED user must never be left on the login page. Waving
    // /login through unconditionally renders the login FORM inside the app
    // shell — the app looks signed in and signed out at once, and only a
    // manual reload clears it. Not an edge case: api.js bounces here on any
    // 401 while the httpOnly cookie usually survives, so the reload lands
    // authenticated; the SSO broker's ?next and plain bookmarks reach it too.
    // Fixed in isg-app-template 2026-07-18; back-propagated 2026-07-31.
    if (path === '/login' && store.get('isAuthenticated')) {
      router.navigate('/');
      return false;
    }
    if (isPublicPath(path)) return true;
    if (!store.get('isAuthenticated')) {
      router.navigate('/login');
      return false;
    }
    return true;
  });

  // Apply branding after every route change
  router.after(() => {
    setTimeout(() => applyBranding(), 50);
  });

  const currentPath = window.location.pathname;
  const isAnonPublicRoute = isPublicPath(currentPath) && currentPath !== '/login';

  if (!hasSession && isAnonPublicRoute) {
    // Anonymous public page (e.g. the QR-code /beta-signup form) — render the
    // route WITHOUT the app shell and WITHOUT bouncing to /login.
    const app = document.getElementById('app');
    app.innerHTML = '<main id="main-content" role="main"></main>';
    await router.resolve();
    applyBranding();
  } else if (!hasSession) {
    // No session — show login
    const app = document.getElementById('app');
    app.innerHTML = '<main id="main-content" role="main"></main>';
    const module = await pages.login();
    module.render(document.getElementById('main-content'));
    if (window.location.pathname !== '/login') {
      window.history.replaceState(null, '', '/login');
    }
    applyBranding();
  } else {
    // Authenticated — render app shell and resolve current route
    await maybeForcePasswordChange();
    renderAppShell();
    checkEnvBanner(); // beta/test banner (no-op on live)
    await router.resolve();
    applyBranding();

    // Check for birthday / work anniversary celebrations (non-blocking)
    checkCelebrations();

    // Mount the Staff Assistant (how-to bot) — visible on every page
    const { renderAIAssistant } = await import('./components/ai-assistant-widget.js');
    const aiMount = document.createElement('div');
    aiMount.id = 'ai-assistant-mount';
    document.body.appendChild(aiMount);
    renderAIAssistant(aiMount);
  }
}

// Boot
init().catch((err) => {
  console.error('Failed to initialize app:', err);
  const root = document.getElementById('app');
  root.innerHTML = `
    <div style="display:flex;align-items:center;justify-content:center;min-height:100vh;flex-direction:column;gap:1rem;">
      <p>${t('common.failed_to_load_page')}</p>
      <button class="btn btn-primary" id="boot-reload-btn">${t('common.reload')}</button>
    </div>
  `;
  root.querySelector('#boot-reload-btn')?.addEventListener('click', () => location.reload());
});
